/**
 * @fileoverview Text widget — single-line input.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render single-line text input with placeholder, maxLength, pattern.
 *
 * Dependencies:
 * - React 18 (peer).
 *
 * Architecture:
 * - Stateless. Parent owns the value via `onChange`.
 *
 * @component TextField
 */
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';

function safeRegex(p: string): RegExp | null {
  try { return new RegExp(p); } catch { return null; }
}

export function TextField(props: BaseWidgetProps<string | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as { placeholder?: string; maxLength?: number; pattern?: string };
  // Active validation — show aria-invalid when value violates pattern or
  // exceeds maxLength. Host can still override via `errorMessage`.
  const patternRe = extras.pattern ? safeRegex(extras.pattern) : null;
  const v = value ?? '';
  const exceedsMax = extras.maxLength != null && v.length > extras.maxLength;
  const patternMismatch = patternRe != null && v.length > 0 && !patternRe.test(v);
  const computedError = errorMessage
    ?? (exceedsMax ? `Max length ${extras.maxLength}` : undefined)
    ?? (patternMismatch ? 'Format does not match pattern' : undefined);
  const isInvalid = !!computedError || (!!requiredUnmet && !!props.requiredImmediate);

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="text"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={computedError}
      requiredUnmet={requiredUnmet} requiredImmediate={props.requiredImmediate}
    >
      <input
        id={field.id}
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={extras.placeholder}
        maxLength={extras.maxLength}
        pattern={extras.pattern}
        disabled={!!resolved?.disabled}
        aria-required={!!(field.required || resolved?.required)}
        aria-invalid={isInvalid}
        className={`w-full rounded-xl px-3 py-2 text-sm outline-hidden focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 disabled:opacity-50 ${isInvalid ? 'border border-red-500 bg-red-50/50 text-red-900 ring-2 ring-red-500/20' : 'border border-slate-300 bg-white text-slate-900'}`}
      />
    </FieldWrapper>
  );
}
